import _Vue from "vue";
import { ErrorBag } from "../../../node_modules/vee-validate";

export class PluginOptions { }

// vee-validate 2 keeps Field.rules as { name: params[] }; converting the incoming rules to that
// shape lets a merge keep params (max_value:100) and de-duplicate rule names.
const toRulesObject = (rules) => {
    if (!rules) return {};
    if (typeof rules !== 'string') return rules;
    return rules.split('|').reduce((parsed, rule) => {
        const [name, ...params] = rule.split(':');
        if (name) parsed[name] = params.length ? params.join(':').split(',') : [];
        return parsed;
    }, {});
};

const ValidationPlugin = {
    install(Vue: typeof _Vue, options?: PluginOptions) {
        //console.log("ValidationPlugin plugin is installing");
        Vue.prototype.selfContext = null;

        Vue.prototype.errors = this.$validator == null ? new ErrorBag() : this.$root.$validator.errors;
        Vue.prototype.fields = this.$validator == null ? [] : this.$root.$validator.fields;
        Vue.prototype.$addValidation = function (modelValidation) {
            //console.log('addValidation');
            if (this.$root.$validator == null) return;
            const self = this as any;
            modelValidation.Fields.forEach(validation => {
                // if field is exist in validator we will not add.
                if (self.$root.$validator.fields.items.find(item => item.name === validation.BindingField && modelValidation.Scope === item.scope)) return;
                
                if (self[validation.BindingField] !== undefined) {
                    let fieldName = validation.DisplayField;
                    if (validation.LocalizedKey) {
                        fieldName = self.$t(validation.LocalizedKey)
                    }
                    self.$root.$validator.attach({
                        name: validation.BindingField,
                        alias: fieldName,
                        rules: validation.Rules,
                        scope: modelValidation.Scope,
                        model: { expression: validation.BindingField, lazy: false, fieldType: validation.FieldType || false },
                        initial: validation.Initial || false,
                        events: "input|blur",
                        vm: {
                            $validator: self.$root.$validator,
                            $watch: self.$watch.bind(self)
                        },
                        getter: () => {
                            return self[validation.BindingField];
                        },
                        flags: {
                            valid: true,
                            invalid: false,
                            validated: false
                        },
                        classes: true,
                        classNames: {
                            valid: "success",
                            invalid: "danger"
                        }
                    });
                }
            });
        };
        Vue.prototype.$addValidationByScope = function (modelValidation, fieldModelContext) {
            if (this.$root.$validator == null) return;
            const self = this as any;
            modelValidation.Fields.forEach(validation => {
                // if field is exist in validator we will not add.
                if (self.$root.$validator.fields.items.find(item => item.name === validation.BindingField && modelValidation.Scope === item.scope)) return;
                
                let fieldName = validation.DisplayField;
                if (validation.LocalizedKey) {
                    fieldName = self.$t(validation.LocalizedKey)
                }
                self.$root.$validator.attach({
                    name: validation.BindingField,
                    alias: fieldName,
                    rules: validation.Rules,
                    scope: modelValidation.Scope,
                    model: { expression: fieldModelContext ? `${fieldModelContext}.${validation.ModelField}` : validation.ModelField, lazy: false, fieldType: validation.FieldType || false },
                    events: "input|blur",
                    vm: {
                        $validator: self.$root.$validator,
                        $watch: self.$watch.bind(self)
                    },
                    getter: () => {
                        return fieldModelContext ? self[fieldModelContext][validation.ModelField] : self[validation.ModelField];
                    },
                    flags: {
                        valid: true,
                        invalid: false,
                        validated: false
                    },
                    classes: true,
                    classNames: {
                        valid: "success",
                        invalid: "danger"
                    }
                });
            });
        };

        Vue.prototype.$removeValidation = function (modelValidation) {
            Vue.prototype.$removeAllCustomValidationFromServer();

            if (this.$root.$validator == null) return;
            const self = this;
            modelValidation.Fields.forEach(validation => {
                self.$root.$validator.detach(validation.BindingField, validation.Scope);
            });
        };

        Vue.prototype.$removeValidationFields = function (fieldsValidation) {
            Vue.prototype.$removeAllCustomValidationFromServer();

            if (this.$root.$validator == null) return;
            const self = this;

            fieldsValidation.forEach(validation => {
                self.$root.$validator.detach(validation.BindingField, validation.Scope);
            });
        };

        Vue.prototype.$resetScopeErrors = function (moduleScope) {
            if (this.$root.$validator == null || !moduleScope) return;
            const items = this.$root.$validator.errors.items;
            const filtered = items.filter(e => {
                const inModule = e.scope && e.scope.indexOf(moduleScope) > -1;
                return !inModule && e.rule !== 'invalid';
            });
            if (filtered.length !== items.length) {
                this.$root.$validator.errors.items = filtered;
            }
        };

        Vue.prototype.$removeScopeValidations = function (scope) {
            Vue.prototype.$removeAllCustomValidationFromServer();

            if (this.$root.$validator == null) return;
            const self = this;
            if (scope != null) scope = scope.toLowerCase();
            const fields = self.$root.$validator.fields.items.filter(
                c => c.scope != null && c.scope.toLowerCase().indexOf(scope) >= 0
            );
            fields.forEach(validation => {
                self.$root.$validator.detach(validation.name, validation.scope);
            });
        };

        Vue.prototype.$validationAll = function () {
            if (!Vue.prototype.$checkCustomValidateFromServer()) {
                return Promise.reject({});
            }

            if (this.$root.$validator == null) return Promise.resolve(true);
            const self = this as any;
            return self.$root.$validator
                .validateScopes()
                .then(result => {
                    if (result) return Promise.resolve(result);
                    return Promise.reject(result);
                })
                .catch(result => {
                    return Promise.reject(result);
                });
        };

        Vue.prototype.$removeAllCustomValidationFromServer = function(){
            const self = Vue.prototype.selfContext;
            if (!self) return;

            self.$root.$validator.errors.items = self.$root.$validator.errors.items.filter(e => e.rule != 'invalid');
            Vue.prototype.selfContext = null;
        }

        Vue.prototype.$checkCustomValidateFromServer = function(){
            const self = Vue.prototype.selfContext;
            if (!self) return true;

            const allError = self.$root.$validator.errors.items || [];
            return !allError.find(e => e.rule == "invalid");
        }

        Vue.prototype.$addCustomValidationFromServer = async function (
            validationFields,
            modelBuilderFactory,
            fieldModelContext,
            self = null
        ) {
            if (!self)  {
                self = this as any;
            }

            validationFields.forEach(item => {
                let field = modelBuilderFactory.Build(item, self.$t);

                const check = self.$root.$validator.errors.items.find(e => e.field == field.ModelField);

                if (!check) {
                    let key = '';
                    if (fieldModelContext && self[fieldModelContext]) {
                        key = fieldModelContext + '.' + field.ModelField;
                    } else {
                        key = field.ModelField;
                    }
                    
                    if (key) {
                        // translateText = validationField.LocalizedKey ? translate(validationField.LocalizedKey) : validationField.DisplayField;
                        setTimeout(()=>{
                            self.$root.$validator.errors.items.push({
                                field: field.ModelField,
                                msg: field.Message,
                                scope: field.Scope,
                                rule: field.Rules,
                                fieldType: field.FieldType,
                            });
                        }, 50)
                        Vue.prototype.selfContext = self;

                        self.$watch(key, (value)=>{
                            console.log("CustomValidationFromServer change: ", value);
                            self.$root.$validator.errors.items = self.$root.$validator.errors.items.filter(e => e.field != field.ModelField);
                        });
                    }
                }
            })
        }

        Vue.prototype.$addValidationFromServer = async function (
            validationFields,
            modelBuilderFactory,
            fieldModelContext,
            moduleScope
        ) {
            const self = this as any;

            const customValidFromServer = validationFields.filter(e => e.Rules === 'invalid' || e.RulesEx === 'invalid');

            if (customValidFromServer && customValidFromServer.length > 0) {
                validationFields = validationFields.filter(e => e.Rules !== 'invalid' && e.RulesEx !== 'invalid');
                Vue.prototype.$addCustomValidationFromServer(customValidFromServer, modelBuilderFactory, fieldModelContext, self);
            }

            if (this.$root.$validator == null) return;
            if (validationFields == null || validationFields.length == 0) return;

            validationFields.forEach(item => {                
                let field = modelBuilderFactory.Build(item, self.$t);   
                if(field === undefined || field == null) return;
                if(field.Scope === undefined || field.Scope == null || field.Scope == "")
                    field.Scope = moduleScope;
                // if field is already in validation set => update rule: attach it to validation set.
                let configValidationField = this.$root.$validator.fields.items.find(
                    c => c.name == field.BindingField && c.scope == field.Scope
                );
                
                if (field.Rules === 'greater_zero') field.Rules = field.Rules + '|required'

                if (configValidationField != null) {
                    configValidationField.setFlags({ invalid: true });
                    configValidationField.update({
                        rules: { ...configValidationField.rules, ...toRulesObject(field.Rules) }
                    })
                    const isInErrorList = this.$root.$validator.errors.items.find(
                        c => c.field == field.BindingField && c.scope == field.Scope
                    );
                    if (!isInErrorList) {
                        self.$root.$validator.errors.items.push({
                            field: field.BindingField,
                            msg: field.Message,
                            scope: field.Scope,
                            rule: field.Rules,
                            fieldType: field.FieldType,
                        });
                    }
                } else {
                    // attach to validation set
                    let fieldName = field.DisplayField;
                    if (field.LocalizedKey) {
                        fieldName = self.$t(field.LocalizedKey)
                    }
                    self.$root.$validator.attach({
                        name: field.BindingField,
                        alias: fieldName,
                        rules: field.Rules,
                        scope: field.Scope,
                        initial: true,
                        events: "input|blur",
                        model: {
                            expression: fieldModelContext ? `${fieldModelContext}.${field.ModelField}` : field.ModelField
                            , lazy: false, 
                            fieldType: field.FieldType || false },
                        vm: {
                            $validator: self.$root.$validator,
                            $watch: self.$watch.bind(self)
                        },
                        getter: () => {
                            return fieldModelContext ? self[fieldModelContext][field.ModelField] : self[field.ModelField]
                        },
                        flags: {
                            valid: false,
                            invalid: true,
                            validated: true
                        },
                        classes: true,
                        classNames: {
                            valid: "success",
                            invalid: "danger"
                        }
                    });
                }
                   
            });
        };

        Vue.prototype.$clearValidations = async function () {
            let self = this;
            if (this.$root.$validator == null) return;
            await this.$root.$validator.reset();
            await this.$root.$validator.errors.clear();
            await this.$root.errorsValidation.clear();
            await this.$root.$validator.resume();

            const fields = this.$root.$validator.fields.items;
            fields.forEach(field => {
                self.$root.$validator.reset(field);
            })
        };

        Vue.prototype.$invalidErrors = function (scope) {
            if (this.$root.$validator == null) return false;
            if (scope == null)
                return this.$root.$validator.errors.items.filter(c => c.scope == null);
            scope = scope.replace(/\[[^\][]*\]$/, "");
            return this.$root.$validator.errors.items.filter(
                c => c.scope.replace(/\[[^\][]*\]$/, "") == scope
            );
        };

        Vue.prototype.$hasScopeInValid = function (scope) {
            if (this.$root.$validator == null) return false;
            if (scope == null)
                return this.$root.$validator.fields.items.some(c => c.flags.validated && c.flags.invalid && c.scope == null);
            scope = scope.replace(/\[[^\][]*\]$/, "");
            return this.$root.$validator.fields.items.some(
                c => c.flags.validated && c.flags.invalid && c.scope.replace(/\[[^\][]*\]$/, "") == scope
            );
        };

        /**
        * Validation all with scope name
        */
        Vue.prototype.$validationAllwithScope = function (scope) {

            if (this.$root.$validator == null) return Promise.resolve(true);
                        
            if (!Vue.prototype.$checkCustomValidateFromServer()) {
                return Promise.reject({});
            }

            const self = this as any;
            return self
                .$validateWithScope(scope)
                .then(result => {
                    if (result) return Promise.resolve(result);
                    return Promise.reject(result);
                })
                .catch(result => {
                    return Promise.reject(result);
                });
        };

        /**
        * Validates with scopes name scopes.
        */
        Vue.prototype.$validateMultipleScope = async function (scopes) {
            if (!Vue.prototype.$checkCustomValidateFromServer()) {
                throw false;
            }
            const self = this;
            if (self.$root.$validator.paused) { 
                return true; 
            }
            
            try {
                for (const scope of scopes) {
                    const fields = self.$root.$validator.fields.items.filter(item => item.scope == scope);
                    
                    const results = [];
                    
                    for (const field of fields) {
                        try {
                            let result = false;
                            if (field.rules && Object.keys(field.rules).includes('required_one_of')) {
                                result = await self.$root.$validator.validate(
                                    ("#" + (field.id)),
                                    field.value || [],
                                );
                            } else {
                                result = await self.$root.$validator.validate(
                                    ("#" + (field.id)),
                                    field.value,
                                );
                            }
                            results.push(result);
                        } catch (error) {
                            results.push(false);
                        }
                    }
                    
                    const scopeValid = results.every(function (t) { return t; });
                    
                    if (!scopeValid) {
                        throw false;
                    }
                }
                
                return true;
            } catch (error) {
                throw false;
            }
        }
        
        Vue.prototype.$validateWithScope = async function (scope) {
            if (!Vue.prototype.$checkCustomValidateFromServer()) {
                throw {};
            }
            const self = this;
        
            if (self.$root.$validator.paused) { 
                return true; 
            }

            const fields = self.$root.$validator.fields.items.filter(item => item.scope == scope);
            const results = [];
            
            for (const field of fields) {

                try {
                    let result = false;
                    if (field.rules && Object.keys(field.rules).includes('required_one_of')) {
                        result = await self.$root.$validator.validate(
                            ("#" + (field.id)),
                            field.value || [],
                        )
                    } else {
                        result = await self.$root.$validator.validate(
                            ("#" + (field.id)),
                            field.value,
                        );
                    }
                    results.push(result);
                } catch (error) {
                    results.push(false);
                }
            }

            return results.every(function (t) { return t; });
        }
    }
};

export default ValidationPlugin;

declare module 'vue/types/vue' {
    interface Vue {
        $validationAllwithScope: (scope: string) => Promise<any>;
        $removeScopeValidations: (scope: string) => Promise<any>;
        $resetScopeErrors: (moduleScope: string) => void;
        $addValidationByScope: (modelValidation, fieldModelContext) => void;
        $validateMultipleScope: (scopes: Array<string>) => Promise<any>;
        $validateWithScope: (scope: string) => Promise<any>;
        $checkCustomValidateFromServer: () => boolean;
    }
}
